import type { Reservation, ReservationItem, Vehicle } from '@/root/shared-types'
import type {
  AddItemsModification,
  DeleteItemRequest,
  DeleteItemsModification,
  TicketRequest,
  VehicleRequest,
} from '../types'
import { groupQuantitiesByPriceCategory } from './group-quantities-by-price-category'
import { isPromotionItem } from '@/root/shared-helpers/reservation/reservation-item-filters'
import { normalizeVehiclePlate } from './normalize-vehicle-plate'

const RANDOM_SEQ_N_RANGE = 10_000_000

interface ItemDiff {
  toAdd: number
  toRemove: number
}

/**
 * Calculate the difference between current and new quantities for a price category
 */
export function calculateItemDiff(currentQty: number, newQty: number): ItemDiff {
  if (newQty > currentQty) {
    return { toAdd: newQty - currentQty, toRemove: 0 }
  } else if (newQty < currentQty) {
    return { toAdd: 0, toRemove: currentQty - newQty }
  }
  return { toAdd: 0, toRemove: 0 }
}

/**
 * Get current item quantities grouped by price category for a sail package
 */
export function getCurrentItemQuantities(
  reservation: Reservation,
  sailPackageSeqN: number,
  itemType: 'PASSENGER' | 'VEHICLE' | 'ADDON'
): Record<string, number> {
  const filtered = (reservation.items ?? []).filter(
    (item) => item.sailPackageSeqN === sailPackageSeqN && item.type === itemType && !isPromotionItem(item)
  )
  return groupQuantitiesByPriceCategory(filtered)
}

/**
 * Build addItems modification for tickets (passengers)
 */
export function buildAddTicketsModification(params: {
  sailPackage: string
  sailRefIds: number[]
  priceCategory: string
  quantity: number
  startSeqN?: number
  personalIdentificationNumber?: string
}): TicketRequest[] {
  const requests: TicketRequest[] = []
  const baseSeqN = params.startSeqN ?? Math.floor(Math.random() * RANDOM_SEQ_N_RANGE)

  for (let i = 0; i < params.quantity; i++) {
    requests.push({
      passenger: {
        priceCategory: params.priceCategory,
        ...(params.personalIdentificationNumber
          ? { personalIdentificationNumber: params.personalIdentificationNumber }
          : {}),
      },
      sailPackage: params.sailPackage,
      sailRefIds: params.sailRefIds,
      seqN: baseSeqN + i,
    })
  }

  return requests
}

/**
 * Build deleteItems modification for tickets
 * A named seqN removes exactly that guest and no one else; otherwise takes seqNs from the END
 * of the owner list (LIFO - last in, first out)
 */
export function buildDeleteTicketsModification(params: {
  sailPackageSeqN: number
  items: ReservationItem[]
  quantity: number
  seqN?: number
}): DeleteItemRequest[] {
  const requests: DeleteItemRequest[] = []

  const allOwnerSeqNs: number[] = []
  params.items.forEach((item) => {
    if (item.ownerSeqNs && !isPromotionItem(item)) {
      allOwnerSeqNs.push(...item.ownerSeqNs)
    }
  })

  // A named guest is the whole request: if this leg's category does not carry them, nothing is
  // removed, where a LIFO fallback would take a stranger's ticket instead.
  const seqNsToRemove =
    params.seqN === undefined
      ? allOwnerSeqNs.slice(-params.quantity)
      : allOwnerSeqNs.filter((seqN) => seqN === params.seqN)

  seqNsToRemove.forEach((seqN) => {
    requests.push({
      sailPackageSeqN: params.sailPackageSeqN,
      seqN,
    })
  })

  return requests
}

/**
 * Build addItems modification for vehicles
 */
export function buildAddVehiclesModification(params: {
  sailPackage: string
  sailRefIds: number[]
  priceCategory: string
  quantity: number
  startSeqN?: number
}): VehicleRequest[] {
  const requests: VehicleRequest[] = []
  const baseSeqN = params.startSeqN ?? Math.floor(Math.random() * RANDOM_SEQ_N_RANGE)

  for (let i = 0; i < params.quantity; i++) {
    requests.push({
      vehicle: {
        priceCategory: params.priceCategory,
        handicapped: false,
      },
      sailPackage: params.sailPackage,
      sailRefIds: params.sailRefIds,
      seqN: baseSeqN + i,
    })
  }

  return requests
}

/**
 * Build deleteItems modification for vehicles
 * Resolves seqNs through the ownerSeqNs of items matching (sailPackageSeqN, priceCategory, type VEHICLE).
 * A matching licencePlate takes priority; otherwise takes from the end (LIFO).
 */
export function buildDeleteVehiclesModification(params: {
  sailPackageSeqN: number
  items: ReservationItem[]
  priceCategory: string
  quantity: number
  vehicles?: Vehicle[]
  licencePlate?: string
}): DeleteItemRequest[] {
  const ownerSeqNs: number[] = []
  params.items
    .filter(
      (item) =>
        item.type === 'VEHICLE' &&
        item.sailPackageSeqN === params.sailPackageSeqN &&
        item.priceCategory === params.priceCategory
    )
    .forEach((item) => {
      if (item.ownerSeqNs) ownerSeqNs.push(...item.ownerSeqNs)
    })

  const wantedPlate = params.licencePlate ? normalizeVehiclePlate(params.licencePlate) : undefined
  const plateMatchedSeqN =
    wantedPlate !== undefined
      ? params.vehicles?.find(
          (v) => normalizeVehiclePlate(v.licencePlate ?? '') === wantedPlate && ownerSeqNs.includes(v.seqN)
        )?.seqN
      : undefined

  // A plate names exactly one vehicle, so a plate-targeted delete is always that one and only that one.
  const seqNsToRemove = plateMatchedSeqN !== undefined ? [plateMatchedSeqN] : ownerSeqNs.slice(-params.quantity)

  return seqNsToRemove.map((seqN) => ({ sailPackageSeqN: params.sailPackageSeqN, seqN }))
}

/**
 * Build modifications from quantity changes
 */
export function buildItemModifications(params: {
  reservation: Reservation
  sailPackageSeqN: number
  sailPackage: string
  sailRefIds: number[]
  newQuantities: {
    passengers?: Record<string, number>
    vehicles?: Record<string, number>
  }
  /** Guest whose ticket the removal is about, when the caller knows it. */
  deleteSeqN?: number
}): Array<AddItemsModification | DeleteItemsModification> {
  const modifications: Array<AddItemsModification | DeleteItemsModification> = []

  // Handle passenger changes
  if (params.newQuantities.passengers) {
    const currentPassengerQty = getCurrentItemQuantities(params.reservation, params.sailPackageSeqN, 'PASSENGER')

    const addTickets: TicketRequest[] = []
    const deleteTickets: DeleteItemRequest[] = []

    const maxGuestSeqN = (params.reservation.guests ?? []).reduce((max, g) => Math.max(max, g.seqN), 0)
    let nextPassengerSeqN = maxGuestSeqN + 1

    Object.entries(params.newQuantities.passengers).forEach(([priceCategory, newQty]) => {
      const currentQty = currentPassengerQty[priceCategory] || 0
      const diff = calculateItemDiff(currentQty, newQty)

      if (diff.toAdd > 0) {
        addTickets.push(
          ...buildAddTicketsModification({
            sailPackage: params.sailPackage,
            sailRefIds: params.sailRefIds,
            priceCategory,
            quantity: diff.toAdd,
            startSeqN: nextPassengerSeqN,
          })
        )
        nextPassengerSeqN += diff.toAdd
      }

      if (diff.toRemove > 0) {
        const itemsForCategory = params.reservation.items?.filter(
          (item) => item.sailPackageSeqN === params.sailPackageSeqN && item.priceCategory === priceCategory
        )

        deleteTickets.push(
          ...buildDeleteTicketsModification({
            sailPackageSeqN: params.sailPackageSeqN,
            items: itemsForCategory || [],
            quantity: diff.toRemove,
            seqN: params.deleteSeqN,
          })
        )
      }
    })

    if (addTickets.length > 0) {
      modifications.push({
        __type: 'addItems',
        ticketsRequests: addTickets,
      })
    }

    if (deleteTickets.length > 0) {
      modifications.push({
        __type: 'deleteItems',
        ticketsRequests: deleteTickets,
      })
    }
  }

  // Handle vehicle changes
  if (params.newQuantities.vehicles) {
    const currentVehicleQty = getCurrentItemQuantities(params.reservation, params.sailPackageSeqN, 'VEHICLE')

    const addVehicles: VehicleRequest[] = []
    const deleteVehicles: DeleteItemRequest[] = []

    const maxVehicleSeqN = (params.reservation.vehicles ?? []).reduce((max, v) => Math.max(max, v.seqN), 0)
    let nextVehicleSeqN = maxVehicleSeqN + 1

    Object.entries(params.newQuantities.vehicles).forEach(([priceCategory, newQty]) => {
      const currentQty = currentVehicleQty[priceCategory] || 0
      const diff = calculateItemDiff(currentQty, newQty)

      if (diff.toAdd > 0) {
        addVehicles.push(
          ...buildAddVehiclesModification({
            sailPackage: params.sailPackage,
            sailRefIds: params.sailRefIds,
            priceCategory,
            quantity: diff.toAdd,
            startSeqN: nextVehicleSeqN,
          })
        )
        nextVehicleSeqN += diff.toAdd
      }

      if (diff.toRemove > 0) {
        deleteVehicles.push(
          ...buildDeleteVehiclesModification({
            sailPackageSeqN: params.sailPackageSeqN,
            items: params.reservation.items ?? [],
            priceCategory,
            quantity: diff.toRemove,
          })
        )
      }
    })

    if (addVehicles.length > 0) {
      modifications.push({
        __type: 'addItems',
        vehiclesRequests: addVehicles,
      })
    }

    if (deleteVehicles.length > 0) {
      modifications.push({
        __type: 'deleteItems',
        vehiclesRequests: deleteVehicles,
      })
    }
  }

  return modifications
}
